import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Confetti from 'react-confetti';
import { toast } from 'react-toastify';
import { useSmartSend } from '../../contexts/SmartSendContext';
import formStyles from '../../styles/SmartSendForm.module.css';
import { revealSku } from '../../services/revealSku.api';
import { isDevelopment, ANALYTICS_SOURCE } from '../../env/config';
import analytics from '../../../../library/segmentAnalytics';
import GiftBox3D from './GiftBox3D';

// ============================================
// CONSTANTS
// ============================================
// Everything happens simultaneously — wobble, shrink, fade, and product reveal.
// The product image starts scaling in at the same time as the box starts shrinking.
const CROSSFADE_START_MS = 0;
// Duration of the product reveal animation (matches the box shrink animation)
const REVEAL_ANIM_DURATION_MS = 2000;

/**
 * SkuRevealStep - Step to ask user if they want to reveal the SKU or keep it a surprise.
 *
 * On click, the API call fires immediately while the box animation plays.
 * As the box shrinks away, the product image scales in simultaneously (crossfade).
 *
 * @param {Function} onReveal - Handler when user chooses to reveal SKU
 * @param {Function} onKeepSurprise - Handler when user chooses to keep it a surprise
 */
const SkuRevealStep = ({ onReveal, onKeepSurprise }) => {
  const { order, orderId, setSecureHeader } = useSmartSend();
  const [isRevealing, setIsRevealing] = useState(false);
  const [hasStartedReveal, setHasStartedReveal] = useState(false);
  const [revealedData, setRevealedData] = useState(null);
  const [revealAnimDelay, setRevealAnimDelay] = useState(0);
  const [transitionDone, setTransitionDone] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [revealFailed, setRevealFailed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
  const clickTimeRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const cardRef = useRef(null);

  // Measure card dimensions for confetti sizing
  const measureCard = useCallback(() => {
    if (cardRef.current) {
      const { width, height } = cardRef.current.getBoundingClientRect();
      setCardSize({ width, height });
    }
  }, []);

  // Measure on mount and resize (confetti needs accurate card dimensions)
  useEffect(() => {
    measureCard();
    window.addEventListener('resize', measureCard);
    return () => window.removeEventListener('resize', measureCard);
  }, [measureCard]);

  // Compute reveal animation delay and schedule transitionDone when revealed data arrives
  useEffect(() => {
    if (revealedData && clickTimeRef.current) {
      const elapsed = Date.now() - clickTimeRef.current;
      const delay = Math.max(0, CROSSFADE_START_MS - elapsed);
      setRevealAnimDelay(delay);

      // Mark transition as done after the reveal animation finishes
      transitionTimerRef.current = setTimeout(() => {
        setTransitionDone(true);
        if (isDevelopment()) {
          console.log('✅ [SmartSend] Crossfade transition complete');
        }
      }, delay + REVEAL_ANIM_DURATION_MS);
    }

    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, [revealedData]);

  const handleReveal = async () => {
    if (isDevelopment()) {
      console.log('🔍 [SmartSend] Order object:', order);
      console.log('🔍 [SmartSend] Order keys:', order ? Object.keys(order) : 'order is null/undefined');
    }

    const ssid = order?.smartSendId;

    if (!ssid) {
      if (isDevelopment()) {
        console.error('❌ [SmartSend] Missing smartSendId. Order:', order);
      }
      toast.error('Missing SmartSend ID. Please try again.');
      return;
    }

    if (isDevelopment()) {
      console.log('🔍 [SmartSend] Revealing SKU:', { ssid, orderId });
    }

    clickTimeRef.current = Date.now();
    setIsRevealing(true);
    setHasStartedReveal(true);
    setRevealFailed(false);
    setImageError(false);

    // Fire confetti on click — re-measure card to get latest dimensions
    measureCard();
    setShowConfetti(true);

    try {
      const result = await revealSku(ssid);

      if (isDevelopment()) {
        console.log('🔍 [SmartSend] Reveal result:', result);
      }

      if (result.success && result.data) {
        setRevealedData(result.data);
        setImageLoading(true);

        // Update nonce from GET response for subsequent POST request
        if (result.newNonce) {
          setSecureHeader(result.newNonce);
          if (isDevelopment()) {
            // Redact nonce for security (only log first 4 chars)
            const redactedNonce =
              typeof result.newNonce === 'string'
                ? `${result.newNonce.slice(0, 4)}...`
                : '[non-string nonce]';
            console.log('✅ [SmartSend] Nonce updated from reveal response (redacted):', redactedNonce);
          }
        }

        // Track analytics
        if (orderId) {
          analytics.track('SmartSend SKU Revealed', {
            source: ANALYTICS_SOURCE,
            orderId,
            hasImage: !!result.data.imageUrl,
            hasTitle: !!result.data.title,
          });
        }

        if (isDevelopment()) {
          console.log('✅ [SmartSend] SKU revealed:', result.data);
        }

        setIsRevealing(false);
      } else {
        if (isDevelopment()) {
          console.error('❌ [SmartSend] Reveal failed:', result);
        }
        toast.error(result.error || 'Failed to reveal SKU. Please try again.');
        setIsRevealing(false);
        setRevealFailed(true);
      }
    } catch (error) {
      console.error('❌ [SmartSend] Error revealing SKU:', error);
      if (isDevelopment()) {
        console.error('Full error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }
      toast.error('An error occurred. Please try again.');
      setIsRevealing(false);
      setRevealFailed(true);
    }
  };

  const handleRetry = () => {
    setRevealFailed(false);
    setHasStartedReveal(false);
    setShowConfetti(false);
    handleReveal();
  };

  const handleKeepSurprise = () => {
    if (orderId) {
      analytics.track('SmartSend SKU Kept Surprise', {
        source: ANALYTICS_SOURCE,
        orderId,
      });
    }

    if (isDevelopment()) {
      console.log('✅ [SmartSend] User chose to keep SKU a surprise');
    }

    onKeepSurprise();
  };

  const handleContinue = () => {
    if (!revealedData) {
      if (isDevelopment()) {
        console.error('❌ [SmartSend] Attempted to continue without revealed data');
      }
      toast.error('Unable to proceed. Please try revealing again.');
      return;
    }

    if (!revealedData.title && !revealedData.imageUrl) {
      if (isDevelopment()) {
        console.warn('⚠️ [SmartSend] Revealed data missing both title and imageUrl:', revealedData);
      }
    }

    if (isDevelopment()) {
      console.log('✅ [SmartSend] User proceeding after viewing revealed SKU');
    }

    if (typeof onReveal === 'function') {
      onReveal(revealedData);
    } else {
      if (isDevelopment()) {
        console.error('❌ [SmartSend] onReveal is not a function:', typeof onReveal);
      }
      toast.error('An error occurred. Please refresh the page.');
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    if (isDevelopment()) {
      console.warn('⚠️ [SmartSend] Failed to load SKU image');
    }
  };

  return (
    <div ref={cardRef} className={formStyles.card}>
      {/* Confetti — absolutely positioned inside card */}
      {showConfetti && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Confetti
            width={cardSize.width}
            height={cardSize.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.15}
            onConfettiComplete={() => setShowConfetti(false)}
          />
        </div>
      )}

      {/* Error recovery — shown when the API call fails after the animation played */}
      {revealFailed ? (
        <>
          <h2 className={formStyles.sectionTitle}>
            Something went wrong
          </h2>
          <div className={formStyles.fieldContainer} style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
            <p className={formStyles.infoText}>
              We couldn&apos;t load your gift details. You can try again or skip ahead.
            </p>
            <button
              className={formStyles.button}
              onClick={handleRetry}
              disabled={isRevealing}
              aria-label="Try revealing the gift again"
            >
              Try Again
            </button>
            <button
              className={formStyles.skipButton}
              onClick={handleKeepSurprise}
              aria-label="Keep the product a surprise and continue"
            >
              &hellip;or keep it a surprise
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Title crossfade container — both titles share the same space */}
          <div className={formStyles.titleCrossfade}>
            {/* Original prompt — scales out on click */}
            {!transitionDone && (
              <h2 className={`${formStyles.sectionTitle} ${formStyles.titleCrossfadeItem}${hasStartedReveal ? ` ${formStyles.giftRevealOut}` : ''}`}>
                Tap to reveal your Gesture!
              </h2>
            )}
            {/* Product title — scales in with the image */}
            {revealedData?.title && (
              <h2
                className={`${formStyles.sectionTitle} ${formStyles.titleCrossfadeItem} ${formStyles.giftRevealIn}`}
                style={{ animationDelay: `${revealAnimDelay}ms`, opacity: 0 }}
              >
                {revealedData.title}
              </h2>
            )}
          </div>

          <div className={formStyles.fieldContainer} style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
            {/* Reveal stage — gift box and product image share the same absolutely-positioned space */}
            <div className={formStyles.revealStage}>
              {/* Gift box — absolutely positioned, handles its own shrink/fade animation */}
              {!transitionDone && (
                <div className={formStyles.revealStageItem}>
                  <GiftBox3D
                    onClick={handleReveal}
                    disabled={isRevealing}
                  />
                </div>
              )}

              {/* Product image — absolutely positioned, scales in as box shrinks out */}
              {revealedData && (
                <div
                  className={`${formStyles.revealStageItem} ${formStyles.giftRevealIn}`}
                  style={{ animationDelay: `${revealAnimDelay}ms`, opacity: 0 }}
                >
                  {revealedData.imageUrl && (
                    <div style={{ minHeight: imageLoading ? '200px' : 'auto' }}>
                      {imageLoading && !imageError && (
                        <div className={formStyles.infoText}>
                          Loading image...
                        </div>
                      )}
                      {imageError ? (
                        <div className={formStyles.infoText} style={{ color: 'var(--color-gray-medium)' }}>
                          Image unavailable
                        </div>
                      ) : (
                        <img
                          src={revealedData.imageUrl}
                          alt={revealedData.title ? `Product: ${revealedData.title}` : 'Revealed product'}
                          onLoad={handleImageLoad}
                          onError={handleImageError}
                          style={{
                            maxWidth: 'var(--max-width-card-sm)',
                            width: '100%',
                            height: 'auto',
                            borderRadius: 'var(--radius-card)',
                            maxHeight: '300px',
                            objectFit: 'contain',
                            display: imageLoading ? 'none' : 'block',
                            margin: '0 auto',
                          }}

                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Button crossfade — both buttons share the same space */}
            <div className={formStyles.buttonCrossfade}>
              {/* "Keep it a surprise" — scales out on click */}
              {!transitionDone && (
                <div className={formStyles.buttonCrossfadeItem}>
                  <button
                    className={`${formStyles.skipButton}${hasStartedReveal ? ` ${formStyles.giftRevealOut}` : ''}`}
                    onClick={handleKeepSurprise}
                    disabled={isRevealing}
                    aria-label="Keep the product a surprise and continue"
                  >
                    &hellip;or keep it a surprise
                  </button>
                </div>
              )}

              {/* Continue button — scales in with the image */}
              {revealedData && (
                <div
                  className={`${formStyles.buttonCrossfadeItem} ${formStyles.giftRevealIn}`}
                  style={{ animationDelay: `${revealAnimDelay}ms`, opacity: 0 }}
                >
                  <button
                    className={formStyles.button}
                    onClick={handleContinue}
                    aria-label="Continue to completion screen"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

SkuRevealStep.propTypes = {
  onReveal: PropTypes.func.isRequired,
  onKeepSurprise: PropTypes.func.isRequired,
};

export default SkuRevealStep;
